import { canonicalSync, SYNC_CHECKPOINT_BYTES, type SyncClock, type VersionV2 } from "@classroom/domain";
import type { CheckpointPointer } from "../../../../../supabase/functions/sync-control/model";
import { digest } from "../../../../../supabase/functions/sync-control/policy";
import { canonical, type ObjectScope, signChallenge, verifyChallenge } from "./cryptoV2";
import { checkDriveCapacity, type DriveFileV2, type SyncDriveV2 } from "./driveV2";
import { packEncrypted, unpackEncrypted } from "./transportV2";

export type CheckpointCell = { version: VersionV2; ancestor?: unknown };
export type ChunkRef = { fileId: string; objectId: string; hash: string; contentHash: string };
export type CheckpointManifest = {
  format: 2;
  datasetId: string;
  generation: string;
  keyVersion: number;
  deviceId: string;
  clock: SyncClock;
  createdAt: number;
  chunks: ChunkRef[];
};
export type CheckpointSource = {
  clock: SyncClock;
  page(after: string | null, limit: number): Promise<{ cells: CheckpointCell[]; after: string | null }>;
};
export type CheckpointSink = {
  begin(id: string, clock: SyncClock): Promise<void>;
  stage(id: string, cells: CheckpointCell[]): Promise<void>;
  commit(id: string): Promise<void>;
  abort(id: string): Promise<void>;
};
const scope = (base: Pick<ObjectScope, "datasetId" | "generation" | "keyVersion">, objectId: string): ObjectScope => ({
  datasetId: base.datasetId,
  generation: base.generation,
  keyVersion: base.keyVersion,
  objectId,
  format: 2,
});
export function checkpointDue(previous: CheckpointPointer | undefined, clock: SyncClock, now: number) {
  const changes = Object.entries(clock).reduce((n, [id, seq]) => n + Math.max(0, seq - (previous?.clock[id] ?? 0)), 0);
  return changes > 0 && (!previous || changes >= 500 || now - previous.createdAt >= 86400000);
}

// Deterministic key pages give unchanged chunks the same content hash. Cipher
// objects stay immutable; only the authenticated control-plane pointer moves.
export async function writeCheckpoint(
  source: CheckpointSource,
  drive: SyncDriveV2,
  base: Pick<ObjectScope, "datasetId" | "generation" | "keyVersion">,
  key: string | CryptoKey,
  signing: CryptoKey,
  deviceId: string,
  inventory: DriveFileV2[],
  previous?: CheckpointManifest,
): Promise<{ pointer: CheckpointPointer; manifest: CheckpointManifest }> {
  const reusable = new Map(previous?.chunks.map((c) => [c.contentHash, c]) ?? []);
  const chunks: ChunkRef[] = [];
  let after: string | null = null;
  const upload = async (value: unknown, kind: "checkpoint" | "chunk") => {
    const objectId = crypto.randomUUID();
    const cipher = await packEncrypted(key, scope(base, objectId), value, SYNC_CHECKPOINT_BYTES);
    const bytes = new TextEncoder().encode(canonical(cipher)).length;
    checkDriveCapacity(inventory, bytes);
    const fileId = await drive.allocateId();
    try {
      await drive.create(fileId, cipher, kind);
    } catch (e) {
      if (!(e instanceof Error) || !["sync-drive-exists", "sync-drive-request-failed"].includes(e.message)) throw e;
    }
    const read = await drive.read(fileId);
    if (!read || canonical(read) !== canonical(cipher)) throw new Error("sync-upload-mismatch");
    await unpackEncrypted(key, scope(base, objectId), read, SYNC_CHECKPOINT_BYTES);
    inventory.push({
      id: fileId,
      size: String(bytes),
      appProperties: {
        dataset: base.datasetId,
        generation: base.generation,
        kind,
        objectId,
        keyVersion: String(base.keyVersion),
      },
    });
    return { fileId, objectId, hash: await digest(read) };
  };
  const emit = async (cells: CheckpointCell[]) => {
    if (!cells.length) return;
    const contentHash = await digest(cells);
    const existing = reusable.get(contentHash);
    chunks.push(existing ?? { ...(await upload({ cells }, "chunk")), contentHash });
  };
  let bucket: CheckpointCell[] = [];
  let bucketBytes = 64;
  do {
    const page = await source.page(after, 256);
    for (const cell of page.cells) {
      const bytes = new TextEncoder().encode(canonicalSync(cell)).length + 1;
      if (bytes + 64 > SYNC_CHECKPOINT_BYTES) throw new Error("sync-checkpoint-cell-limit");
      if (bucketBytes + bytes > SYNC_CHECKPOINT_BYTES) {
        await emit(bucket);
        bucket = [];
        bucketBytes = 64;
      }
      bucket.push(cell);
      bucketBytes += bytes;
    }
    if (page.after === after && page.after !== null) throw new Error("sync-checkpoint-cursor");
    after = page.after;
  } while (after !== null);
  await emit(bucket);
  const manifest: CheckpointManifest = {
    format: 2,
    datasetId: base.datasetId,
    generation: base.generation,
    keyVersion: base.keyVersion,
    deviceId,
    clock: source.clock,
    createdAt: Date.now(),
    chunks,
  };
  const signed = { manifest, signature: await signChallenge(signing, manifest) };
  const uploaded = await upload(signed, "checkpoint");
  return { pointer: { ...uploaded, clock: manifest.clock, createdAt: manifest.createdAt }, manifest };
}

export async function checkpointManifest(
  pointer: CheckpointPointer,
  drive: SyncDriveV2,
  base: Pick<ObjectScope, "datasetId" | "generation" | "keyVersion">,
  key: CryptoKey,
  publicKeys: { id: string; signing: JsonWebKey }[],
) {
  const cipher = await drive.read(pointer.fileId);
  if (!cipher || (await digest(cipher)) !== pointer.hash) throw new Error("sync-checkpoint-integrity");
  const signed = (await unpackEncrypted(key, scope(base, pointer.objectId), cipher, SYNC_CHECKPOINT_BYTES)) as {
    manifest: CheckpointManifest;
    signature: string;
  };
  const m = signed?.manifest;
  if (
    !m ||
    m.format !== 2 ||
    m.datasetId !== base.datasetId ||
    m.generation !== base.generation ||
    m.keyVersion !== base.keyVersion ||
    canonical(m.clock) !== canonical(pointer.clock) ||
    m.createdAt !== pointer.createdAt ||
    !Array.isArray(m.chunks) ||
    m.chunks.length > 100000 ||
    new Set(m.chunks.map((c) => c.fileId)).size !== m.chunks.length
  )
    throw new Error("sync-checkpoint-format");
  const publisher = publicKeys.find((d) => d.id === m.deviceId);
  if (!publisher) throw new Error("sync-operation-signature");
  const pub = await crypto.subtle.importKey("jwk", publisher.signing, { name: "ECDSA", namedCurve: "P-256" }, false, [
    "verify",
  ]);
  if (!(await verifyChallenge(pub, m, signed.signature))) throw new Error("sync-operation-signature");
  return m;
}
export async function readCheckpoint(
  pointer: CheckpointPointer,
  drive: SyncDriveV2,
  base: Pick<ObjectScope, "datasetId" | "generation" | "keyVersion">,
  key: CryptoKey,
  publicKeys: { id: string; signing: JsonWebKey }[],
  sink: CheckpointSink,
  progress: (completed: number, total: number) => void = () => {},
) {
  const m = await checkpointManifest(pointer, drive, base, key, publicKeys);
  progress(0, m.chunks.length);
  await sink.begin(pointer.objectId, m.clock);
  try {
    let completed = 0;
    for (const ref of m.chunks) {
      if (
        !ref ||
        typeof ref.fileId !== "string" ||
        typeof ref.objectId !== "string" ||
        typeof ref.hash !== "string" ||
        typeof ref.contentHash !== "string"
      )
        throw new Error("sync-checkpoint-format");
      const object = await drive.read(ref.fileId);
      if (!object || (await digest(object)) !== ref.hash) throw new Error("sync-checkpoint-integrity");
      const value = (await unpackEncrypted(key, scope(base, ref.objectId), object, SYNC_CHECKPOINT_BYTES)) as {
        cells: CheckpointCell[];
      };
      if (!Array.isArray(value?.cells) || (await digest(value.cells)) !== ref.contentHash)
        throw new Error("sync-checkpoint-integrity");
      await sink.stage(pointer.objectId, value.cells);
      progress(++completed, m.chunks.length);
    }
    await sink.commit(pointer.objectId);
  } catch (error) {
    await sink.abort(pointer.objectId);
    throw error;
  }
  return m;
}
